import {Component, computed, effect, input, signal, untracked} from '@angular/core';
import {Router} from '@angular/router';
import {BorService} from '../../../../sdk/bor-api';
import {SearchCompanyItem} from '../../../../sdk/bor-api/models/models/search-company-item';
import {parseDDMMYYYYDate} from '../../../../utils/time-utils';
import {finalize} from 'rxjs';
import {SearchCompanyResponse} from '../../../../sdk/bor-api/models/models/search-company-response';
import {getCompanyTypesById} from '../../../../utils/datasets/business-classifications.dataset';
import {ToastrService} from '../../../../shared/services/toast/toastr.service';

@Component({
  selector: 'bor-search-result',
  standalone: false,
  templateUrl: './search-result.component.html',
  styleUrl: './search-result.component.scss',
})
export class SearchResultComponent {
  private numberPerPage = 10;
  searchQuery = input<string>('', {alias: 'q'});
  page = input<string>('1');

  pageNumber = computed(() => parseInt(this.page(), 10) || 1);

  searchTerm = signal('');
  hasError = false;

  companyList = signal<SearchCompanyItem[]>([]);
  loading = signal<boolean>(true);
  forceShowTotal = signal<boolean>(false);

  searchResponse = signal<SearchCompanyResponse | null>(null);

  get canGoNext(): boolean {
    return !!this.searchResponse()?.pagination?.hasNext;
  }

  get canGoBack(): boolean {
    return !!this.searchResponse()?.pagination?.hasPrevious;
  }

  constructor(
    private router: Router,
    private bor: BorService,
    private toastr: ToastrService,
  ) {
    effect(() => {
      this.searchTerm.set(this.searchQuery());
    });

    effect(() => {
      // 1. Explicitly read ONLY the signals that should trigger the search
      const query = this.searchQuery();
      const page = this.pageNumber();

      // 2. Run everything else untracked so other signals are ignored
      untracked(() => {
        this.doSearch(query, page);
      });
    });
  }

  getDate(date:any){
    return new Date(parseDDMMYYYYDate(date));
  }

  getCompanyType(id:number){
    return getCompanyTypesById(id)?.name?.replaceAll("_", " ") || '';
  }

  goToPage(page: number) {
    window.scrollTo({top: 0, behavior: 'instant'});
    this.forceShowTotal.set(true);
    this.router.navigate([], {
      queryParams: { page },
      queryParamsHandling: 'merge'
    });
  }

  onUpdateSearchQuery(){
    const searchQuery = this.searchTerm()?.trim() || '';
    if (searchQuery === '') {
      this.hasError = true;
      return;
    }

    this.router.navigate([], {
      queryParams: { q: searchQuery, page: 1 },
      queryParamsHandling: 'merge'
    });
  }

  doSearch(searchTerm: string, page: number){
    this.loading.set(true);
    if (!this.forceShowTotal()){ this.searchResponse.set(null); }

    this.bor.apiV1BorCompaniesSearchGet({
      searchTerm, page,
      size: this.numberPerPage
    })
      .pipe(finalize(() => {
        this.loading.set(false);
        this.forceShowTotal.set(false);
      }))
      .subscribe({
        next: (res) => {
          this.searchResponse.set(res);
          this.companyList.set(res?.companyList || []);

          if (!!res?.pagination?.totalPages && (this.pageNumber() > res.pagination.totalPages)){
            setTimeout(()=>{this.goToPage(res.pagination?.totalPages!)}, 600)
          }
        },
        error: (err) => {
          this.toastr.showDanger(err?.error?.message || `Failed to search for "${searchTerm}"`);
          this.searchResponse.set(null);
          this.companyList.set([]);
        },
      })
  }

}
